import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Bot, Plus, Trash2, Loader2, Save, GripVertical, Paperclip, X, Lock, Users, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Step Component with File Attachments
function SortableStepItem({ step, index, updateStep, removeStep, stepsLength, onAttachFile, onRemoveAttachment, fileInputRef, isUploading }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: step.step_number });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return '🖼️';
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('json')) return '📋';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div ref={setNodeRef} style={style} className="glass-card p-4 rounded-sm">
      <div className="flex items-start gap-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="w-8 h-8 bg-[#00E599]/10 rounded-sm flex items-center justify-center flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-5 h-5 text-[#00E599]" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-800 text-white border-slate-700">
            Drag to reorder
          </TooltipContent>
        </Tooltip>
        <div className="w-8 h-8 bg-[#00E599]/10 rounded-sm flex items-center justify-center flex-shrink-0 mt-1">
          <span className="text-[#00E599] font-bold">{step.step_number}</span>
        </div>
        <div className="flex-1 space-y-3">
          <Input
            data-testid={`step-description-${index}`}
            placeholder="Step description (e.g., Click login button)"
            className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
            value={step.description || ''}
            onChange={(e) => updateStep(index, 'description', e.target.value)}
            required
          />
          <Input
            data-testid={`step-expected-${index}`}
            placeholder="Expected result — e.g.: 'Welcome' text appears, URL contains /dashboard, error message is not visible"
            className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm placeholder:text-slate-600"
            value={step.expected_result || ''}
            onChange={(e) => updateStep(index, 'expected_result', e.target.value)}
          />
          
          {/* File Attachments Section */}
          <div className="pt-2 border-t border-white/5">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-500 flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                Attachments (for file upload actions)
              </Label>
              <div>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  ref={fileInputRef}
                  onChange={(e) => onAttachFile(index, e.target.files)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-[#00E599] hover:text-[#00CC88] text-xs h-7"
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  disabled={isUploading}
                  data-testid={`attach-file-btn-${index}`}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Plus className="w-3 h-3 mr-1" />
                      Attach File
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Display attached files */}
            {step.attachments && step.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {step.attachments.map((attachment, attIndex) => (
                  <Badge
                    key={attIndex}
                    className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 pr-1 flex items-center gap-1"
                  >
                    <span>{getFileIcon(attachment.content_type)}</span>
                    <span className="max-w-[120px] truncate">{attachment.filename}</span>
                    <span className="text-[10px] text-slate-400">({formatFileSize(attachment.size)})</span>
                    <button
                      type="button"
                      onClick={() => onRemoveAttachment(index, attIndex)}
                      className="ml-1 hover:bg-[#D946EF]/20 rounded p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {stepsLength > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-testid={`remove-step-btn-${index}`}
                type="button"
                size="icon"
                variant="ghost"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm flex-shrink-0"
                onClick={() => removeStep(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-800 text-white border-slate-700">
              Remove Step
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}

export default function EditTest() {
  const navigate = useNavigate();
  const { testCaseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRefs = useRef({});

  // Form state
  const [testName, setTestName] = useState("");
  const [testDescription, setTestDescription] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [browser, setBrowser] = useState("chromium");
  const [selfHealing, setSelfHealing] = useState(true); // Phase A
  const [steps, setSteps] = useState([]);
  const [isSharedWithTeam, setIsSharedWithTeam] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track which steps are currently uploading a file
  const [uploadingSteps, setUploadingSteps] = useState(new Set());
  const [originalData, setOriginalData] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchTestCase();
    fetchUserTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testCaseId]);
  
  const fetchUserTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setUserTeams(response.data?.teams || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };
  
  // Detect unsaved changes — compare each field explicitly so that
  // extra backend fields (MongoDB _id, etc.) or key-order differences
  // in JSON.stringify never cause false "no change" results.
  useEffect(() => {
    if (!originalData) return;

    // Flat fields
    const flatChanged =
      testName          !== (originalData.name              || '')       ||
      testDescription   !== (originalData.description       || '')       ||
      targetUrl         !== (originalData.target_url        || '')       ||
      browser           !== (originalData.browser           || 'chromium') ||
      isSharedWithTeam  !== (originalData.is_shared_with_team || false)  ||
      selfHealing       !== (originalData.self_healing !== false); // Phase A

    if (flatChanged) { setHasUnsavedChanges(true); return; }

    // Steps — only compare the user-editable fields; ignore backend-only
    // metadata so unknown fields never mask real edits.
    if (steps.length !== originalData.steps.length) {
      setHasUnsavedChanges(true); return;
    }

    const stepsChanged = steps.some((step, i) => {
      const orig = originalData.steps[i];
      return (
        (step.description    || '') !== (orig.description    || '') ||
        (step.expected_result || '') !== (orig.expected_result || '') ||
        step.step_number             !== orig.step_number            ||
        JSON.stringify(step.attachments || []) !== JSON.stringify(orig.attachments || [])
      );
    });

    setHasUnsavedChanges(stepsChanged);
  }, [testName, testDescription, targetUrl, browser, steps, isSharedWithTeam, selfHealing, originalData]);
  
  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchTestCase = async () => {
    try {
      const response = await axios.get(`${API}/test-cases/${testCaseId}`, { withCredentials: true });
      const tc = response.data;
      setTestName(tc.name);
      setTestDescription(tc.description || '');
      setTargetUrl(tc.target_url || '');
      setBrowser(tc.browser || 'chromium');
      // Ensure steps have attachments array
      const stepsWithAttachments = tc.steps.map(s => ({
        ...s,
        attachments: s.attachments || []
      }));
      setSteps(stepsWithAttachments);
      setIsSharedWithTeam(tc.is_shared_with_team || false);
      setSelfHealing(tc.self_healing !== false); // Phase A: default true

      // Deep-clone steps for change detection so shared object references
      // never cause originalData to drift when current steps are mutated.
      setOriginalData({
        name: tc.name,
        description: tc.description || '',
        target_url: tc.target_url || '',
        browser: tc.browser || 'chromium',
        steps: JSON.parse(JSON.stringify(stepsWithAttachments)),
        is_shared_with_team: tc.is_shared_with_team || false,
        self_healing: tc.self_healing !== false // Phase A
      });
    } catch (error) {
      console.error('Failed to fetch test case:', error);
      toast.error('Failed to load test case');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, { step_number: steps.length + 1, description: "", expected_result: "", attachments: [] }]);
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    const renumbered = newSteps.map((step, i) => ({ ...step, step_number: i + 1 }));
    setSteps(renumbered);
  };

  const updateStep = (index, field, value) => {
    // Functional update avoids stale closure: we always operate on the
    // latest steps snapshot, not the one captured when this function was
    // created.  Spread creates a NEW step object so originalData is never
    // mutated → change-detector correctly sees a diff → Save button enables.
    setSteps(prev => prev.map((step, i) =>
      i === index ? { ...step, [field]: value } : step
    ));
  };

  // Handle file attachment for a specific step
  const handleStepFileUpload = async (stepIndex, files) => {
    if (!files || files.length === 0) return;

    // Show loader on this step's attach button
    setUploadingSteps(prev => new Set([...prev, stepIndex]));

    const newAttachments = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload-step-file`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });

        newAttachments.push(response.data);
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(`Failed to upload "${file.name}"`);
      }
    }

    if (newAttachments.length > 0) {
      setSteps(prev => prev.map((step, i) =>
        i === stepIndex
          ? { ...step, attachments: [...(step.attachments || []), ...newAttachments] }
          : step
      ));
      toast.success(`${newAttachments.length} file(s) attached`);
    }

    // Hide loader
    setUploadingSteps(prev => {
      const next = new Set(prev);
      next.delete(stepIndex);
      return next;
    });
  };

  const removeAttachment = (stepIndex, attachmentIndex) => {
    setSteps(prev => prev.map((step, i) =>
      i === stepIndex
        ? { ...step, attachments: step.attachments.filter((_, ai) => ai !== attachmentIndex) }
        : step
    ));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.step_number === active.id);
        const newIndex = items.findIndex((item) => item.step_number === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Renumber steps
        return newItems.map((step, i) => ({ ...step, step_number: i + 1 }));
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testName || steps.some(s => !s.description)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/test-cases/${testCaseId}`, {
        name: testName,
        description: testDescription,
        target_url: targetUrl,
        browser: browser,
        steps: steps,
        is_shared_with_team: isSharedWithTeam,
        self_healing: selfHealing  // Phase A
      }, { withCredentials: true });

      // Deep-clone steps so originalData never shares object references
      // with the current steps state — shared refs cause the change
      // detector to always see zero diff and keep Save disabled.
      setOriginalData({
        name: testName,
        description: testDescription,
        target_url: targetUrl,
        browser: browser,
        steps: JSON.parse(JSON.stringify(steps)),
        is_shared_with_team: isSharedWithTeam,
        self_healing: selfHealing  // Phase A
      });
      setHasUnsavedChanges(false);
      
      toast.success("Test case updated successfully!");
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to update test case:', error);
      toast.error("Failed to update test case");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] text-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#00E599] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="edit-test-page">
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="px-6 py-6 md:px-12 border-b border-white/5">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-[#00E599] rounded-sm flex items-center justify-center">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>AutoQA.ai</h1>
            </div>
            <Button 
              data-testid="nav-dashboard-btn"
              variant="ghost"
              className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </nav>

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Edit Test Case
              </h1>
              <div className="flex items-center gap-3 mb-12">
                <p className="text-xl text-slate-400">
                  Modify your test case. Drag steps to reorder them.
                </p>
                {hasUnsavedChanges && (
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-sm rounded-full border border-amber-500/30 animate-pulse">
                    Unsaved changes
                  </span>
                )}
              </div>

              <Card className="glass-card border-white/5 rounded-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="test-name" className="text-slate-300 mb-2 block">Test Name *</Label>
                        <Input
                          id="test-name"
                          data-testid="test-name-input"
                          placeholder="Login Test"
                          className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                          value={testName}
                          onChange={(e) => setTestName(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="target-url" className="text-slate-300 mb-2 block">Target URL</Label>
                        <Input
                          id="target-url"
                          data-testid="target-url-input"
                          placeholder="https://example.com"
                          className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                          value={targetUrl}
                          onChange={(e) => setTargetUrl(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="test-description" className="text-slate-300 mb-2 block">Description</Label>
                      <Textarea
                        id="test-description"
                        data-testid="test-description-input"
                        placeholder="Describe what this test validates..."
                        className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm"
                        value={testDescription}
                        onChange={(e) => setTestDescription(e.target.value)}
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300 mb-2 block">Browser</Label>
                      <Select value={browser} onValueChange={setBrowser}>
                        <SelectTrigger data-testid="browser-select" className="bg-black/40 border-white/10 focus:border-[#00E599] text-white rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0F1A] border-white/10">
                          <SelectItem value="chromium">Chromium</SelectItem>
                          <SelectItem value="firefox">Firefox</SelectItem>
                          <SelectItem value="webkit">WebKit (Safari)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Phase A: Self-Healing Toggle */}
                    <div data-testid="self-healing-wrapper" className="p-4 bg-black/30 border border-white/10 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <Wrench className="w-5 h-5 text-[#00E599] mt-0.5" />
                          <div>
                            <Label className="text-slate-200 font-medium">Self-Healing</Label>
                            <p className="text-xs text-slate-500 mt-1">
                              {selfHealing
                                ? 'AI will auto-fix broken selectors during execution'
                                : 'Self-healing is off — broken selectors will fail the step'}
                            </p>
                          </div>
                        </div>
                        <Switch
                          data-testid="self-healing-toggle"
                          checked={selfHealing}
                          onCheckedChange={setSelfHealing}
                        />
                      </div>
                    </div>

                    {/* Privacy / Share with Team */}
                    {userTeams.length > 0 && (
                      <div data-testid="privacy-toggle-wrapper" className="p-4 bg-black/30 border border-white/10 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            {isSharedWithTeam ? (
                              <Users className="w-5 h-5 text-[#00E599] mt-0.5" />
                            ) : (
                              <Lock className="w-5 h-5 text-slate-400 mt-0.5" />
                            )}
                            <div>
                              <Label className="text-slate-200 font-medium">
                                {isSharedWithTeam ? 'Shared with Team' : 'Private'}
                              </Label>
                              <p className="text-xs text-slate-500 mt-1">
                                {isSharedWithTeam
                                  ? 'Visible to all your team members on the Shared Dashboard'
                                  : 'Only visible to you (and your team owner/leader)'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            data-testid="share-with-team-toggle"
                            checked={isSharedWithTeam}
                            onCheckedChange={setIsSharedWithTeam}
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-slate-300">Test Steps * <span className="text-xs text-slate-500">(drag to reorder)</span></Label>
                        <Button 
                          data-testid="add-step-btn"
                          type="button"
                          size="sm"
                          className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm"
                          onClick={addStep}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Step
                        </Button>
                      </div>

                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={steps.map(s => s.step_number)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-4">
                            {steps.map((step, index) => (
                              <SortableStepItem
                                key={step.step_number}
                                step={step}
                                index={index}
                                updateStep={updateStep}
                                removeStep={removeStep}
                                stepsLength={steps.length}
                                onAttachFile={handleStepFileUpload}
                                onRemoveAttachment={removeAttachment}
                                fileInputRef={{ current: fileInputRefs.current[index] }}
                                isUploading={uploadingSteps.has(index)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      
                      {/* Add Step at Bottom */}
                      {steps.length > 0 && (
                        <div className="mt-4 flex justify-center">
                          <Button 
                            data-testid="add-step-bottom-btn"
                            type="button"
                            variant="outline"
                            className="border-dashed border-white/20 text-slate-400 hover:text-white hover:border-[#00E599] hover:bg-[#00E599]/5 rounded-sm"
                            onClick={addStep}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Step Below
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      {hasUnsavedChanges && (
                        <Button 
                          data-testid="discard-btn"
                          type="button"
                          variant="outline"
                          className="flex-1 border-white/10 text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                          onClick={() => {
                            if (originalData) {
                              setTestName(originalData.name);
                              setTestDescription(originalData.description);
                              setTargetUrl(originalData.target_url);
                              setBrowser(originalData.browser);
                              setSelfHealing(originalData.self_healing !== false); // Phase A
                              // Deep-clone so steps never shares references
                              // with originalData.steps after discard
                              setSteps(JSON.parse(JSON.stringify(originalData.steps)));
                              toast.info('Changes discarded');
                            }
                          }}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Discard Changes
                        </Button>
                      )}
                      <Button 
                        data-testid="save-btn"
                        type="submit" 
                        className={`${hasUnsavedChanges ? 'flex-1' : 'w-full'} rounded-sm font-semibold ${
                          hasUnsavedChanges 
                            ? 'bg-[#00E599] text-black hover:bg-[#00CC88] neon-glow' 
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                        disabled={saving || !hasUnsavedChanges}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            {hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
